// Meta (Facebook) App Events — native only (iOS + Android via Capacitor).
// All calls are guarded so they are safe no-ops on web/SSR and never break the web build.
// Standard events use Meta's exact native names + parameter keys (_valueToSum, fb_currency).
import { Capacitor } from '@capacitor/core';

let _fb = null;
let _att = null;
let _initStarted = false;
const _loggedTxns = new Set(); // de-dupe Subscribe by RevenueCat transaction id

const isNative = () => Capacitor.isNativePlatform();

async function fb() {
  if (_fb) return _fb;
  const mod = await import('@capacitor-community/facebook-login');
  _fb = mod.FacebookLogin;
  return _fb;
}

async function att() {
  if (_att) return _att;
  const mod = await import('capacitor-plugin-app-tracking-transparency');
  _att = mod.AppTrackingTransparency;
  return _att;
}

// Call once after the app has loaded and (ideally) after your privacy/consent screen.
// Enables automatic app-install + app-open logging, and gates advertiser-ID collection on ATT consent (iOS).
export async function initMetaAppEvents() {
  if (!isNative() || _initStarted) return;
  _initStarted = true;
  try {
    const FacebookLogin = await fb();
    // Automatic app install / app open logging.
    await FacebookLogin.setAutoLogAppEventsEnabled({ enabled: true });

    if (Capacitor.getPlatform() === 'ios') {
      let granted = false;
      try {
        const AppTrackingTransparency = await att();
        const status = await AppTrackingTransparency.getStatus();
        if (status.status === 'notDetermined') {
          const res = await AppTrackingTransparency.requestPermission();
          granted = res.status === 'authorized';
        } else {
          granted = status.status === 'authorized';
        }
      } catch (e) {
        granted = false;
      }
      await FacebookLogin.setAdvertiserIDCollectionEnabled({ enabled: granted });
      if (typeof FacebookLogin.setAdvertiserTrackingEnabled === 'function') {
        await FacebookLogin.setAdvertiserTrackingEnabled({ enabled: granted });
      }
    } else {
      // Android: enable advertiser-ID collection (respect your own consent decision here if needed).
      await FacebookLogin.setAdvertiserIDCollectionEnabled({ enabled: true });
    }
  } catch (e) {
    // SDK not configured / not available — stay silent, never block the app.
  }
}

async function logEvent(eventName, parameters) {
  if (!isNative()) return;
  try {
    const FacebookLogin = await fb();
    await FacebookLogin.logEvent(parameters ? { eventName, parameters } : { eventName });
  } catch (e) {
    /* no-op */
  }
}

// CompleteRegistration — call when a user finishes sign-up.
export function metaCompleteRegistration(method = 'phone') {
  return logEvent('fb_mobile_complete_registration', { fb_registration_method: String(method) });
}

// StartTrial — call when a user starts a free trial.
export function metaStartTrial(valueUsd = 0) {
  return logEvent('StartTrial', { _valueToSum: Number(valueUsd) || 0, fb_currency: 'USD' });
}

// Subscribe — call when a user subscribes to Plus/Pro. Include value + USD currency.
export function metaSubscribe(amountUsd, transactionId) {
  if (transactionId) {
    if (_loggedTxns.has(transactionId)) return Promise.resolve();
    _loggedTxns.add(transactionId);
  }
  const params = { _valueToSum: Number(amountUsd) || 0, fb_currency: 'USD' };
  if (transactionId) params.fb_order_id = String(transactionId);
  return logEvent('Subscribe', params);
}

// ShareApp — custom event when a user shares/invites from the app (growth loop analytics).
export function metaShareApp() {
  return logEvent('ShareApp');
}
